import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardBody, Chip, Spinner } from '@heroui/react';
import {
  Plus,
  FolderOpen,
  LayoutDashboard,
  Package,
  Wrench,
  Box,
  ArrowRight,
  Clock,
  FileText,
} from 'lucide-react';
import { useProjects } from '@/api/projects';
import { useDashboardStats } from '@/api/dashboard';
import NewProjectModal from '@/components/dashboard/NewProjectModal';
import type { ProjectStatusEnum } from '@estimator/shared';

// ─── Status config ────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  string,
  { label: string; color: string; chipColor: 'default' | 'primary' | 'warning' | 'success' | 'secondary' }
> = {
  NEW: { label: 'New', color: 'var(--text-muted)', chipColor: 'default' },
  IN_PROGRESS: { label: 'In Progress', color: 'var(--accent)', chipColor: 'primary' },
  REVIEW: { label: 'Review', color: '#f59e0b', chipColor: 'warning' },
  COMPLETED: { label: 'Completed', color: '#22c55e', chipColor: 'success' },
  ON_HOLD: { label: 'On Hold', color: '#a78bfa', chipColor: 'secondary' },
};

const STATUS_ORDER: string[] = ['NEW', 'IN_PROGRESS', 'REVIEW', 'COMPLETED', 'ON_HOLD'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

function relativeDate(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function DashboardPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const { data: projectsData, isLoading: projectsLoading } = useProjects({ page: 1 });
  const { data: stats, isLoading: statsLoading } = useDashboardStats();

  const projects = projectsData?.data ?? [];
  const isLoading = projectsLoading || statsLoading;

  return (
    <div
      className="min-h-full px-4 sm:px-6 lg:px-8 py-8 flex flex-col gap-8"
      style={{ backgroundColor: 'var(--bg-base)' }}
    >
      {/* ─── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border)' }}
          >
            <LayoutDashboard size={20} style={{ color: 'var(--accent)' }} />
          </div>
          <div>
            <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
              Dashboard
            </h1>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-secondary)' }}>
              System overview and quick access
            </p>
          </div>
        </div>

        <Button
          color="primary"
          onPress={() => setIsModalOpen(true)}
          startContent={<Plus size={16} />}
          className="hidden sm:flex font-medium"
        >
          New Project
        </Button>
      </div>

      {/* ─── KPI Stats Row ───────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-24 rounded-xl animate-pulse"
              style={{ backgroundColor: 'var(--bg-surface)' }}
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total Projects" value={formatNumber(stats?.total_projects ?? projects.length)} />
          <StatCard label="Total Cabinets" value={formatNumber(stats?.total_cabinets ?? 0)} />
          <StatCard label="Materials in Library" value={formatNumber(stats?.materials_in_library ?? 0)} />
          <StatCard label="Hardware Items" value={formatNumber(stats?.hardware_in_library ?? 0)} />
        </div>
      )}

      {/* ─── Project Status Breakdown ─────────────────────────────────────── */}
      {!isLoading && stats && (
        <div className="flex flex-col gap-3">
          <h2 className="text-sm font-medium uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
            Project Pipeline
          </h2>
          <StatusBreakdown breakdown={stats.status_breakdown} total={stats.total_projects} />
        </div>
      )}

      {/* ─── Main Grid: Recent Activity + Library Cards ───────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity (2/3 width) */}
        <div className="lg:col-span-2 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
              Recent Activity
            </h2>
            {projects.length > 0 && (
              <Button
                size="sm"
                variant="light"
                onPress={() => navigate('/projects')}
                endContent={<ArrowRight size={14} />}
                className="text-xs"
                style={{ color: 'var(--text-secondary)' }}
              >
                View All
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner size="lg" style={{ color: 'var(--accent)' }} />
            </div>
          ) : stats && stats.recent_projects.length > 0 ? (
            <div className="flex flex-col gap-2">
              {stats.recent_projects.map((p) => (
                <RecentProjectRow key={p.id} project={p} onOpen={() => navigate(`/projects/${p.id}`)} />
              ))}
            </div>
          ) : (
            <EmptyState onNew={() => setIsModalOpen(true)} />
          )}
        </div>

        {/* Library Quick-Access (1/3 width) */}
        <div className="flex flex-col gap-3">
          <h2 className="text-sm font-medium uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
            Libraries
          </h2>
          <div className="flex flex-col gap-2">
            <LibraryCard
              label="Materials Library"
              count={stats?.materials_in_library}
              icon={<Package size={18} />}
              href="/settings/materials"
            />
            <LibraryCard
              label="Hardware Library"
              count={stats?.hardware_in_library}
              icon={<Wrench size={18} />}
              href="/settings/hardware"
            />
            <LibraryCard
              label="Cabinet Templates"
              count={stats?.cabinet_templates}
              icon={<Box size={18} />}
              href="/settings/templates"
            />
          </div>
        </div>
      </div>

      {/* ─── All Projects Grid ─────────────────────────────────────────── */}
      {!isLoading && projects.length > 0 && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
              All Projects
            </h2>
            <span className="text-xs font-ui" style={{ color: 'var(--text-muted)' }}>
              {projectsData?.total ?? projects.length} total
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {projects.map((project) => (
              <ProjectMiniCard key={project.id} project={project} />
            ))}
          </div>
        </div>
      )}

      {/* ─── FAB — mobile ────────────────────────────────────────────────── */}
      <button
        onClick={() => setIsModalOpen(true)}
        aria-label="New Project"
        className="sm:hidden fixed bottom-6 right-6 w-14 h-14 rounded-full flex items-center justify-center shadow-lg z-40 focus:outline-none transition-opacity hover:opacity-90 active:scale-95"
        style={{ backgroundColor: 'var(--accent)', color: '#0f1012' }}
      >
        <Plus size={24} />
      </button>

      <NewProjectModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Card
      classNames={{ base: 'border border-[var(--border)]' }}
      style={{ backgroundColor: 'var(--bg-surface)' }}
    >
      <CardBody className="p-5">
        <div className="flex flex-col gap-1">
          <p className="text-xs uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
            {label}
          </p>
          <span className="text-3xl font-bold font-ui mt-1" style={{ color: 'var(--text-primary)' }}>
            {value}
          </span>
        </div>
      </CardBody>
    </Card>
  );
}

function StatusBreakdown({ breakdown, total }: { breakdown: Record<string, number>; total: number }) {
  if (total === 0) return null;

  return (
    <Card
      classNames={{ base: 'border border-[var(--border)]' }}
      style={{ backgroundColor: 'var(--bg-surface)' }}
    >
      <CardBody className="p-5 flex flex-col gap-4">
        {/* Segmented bar */}
        <div className="flex rounded-full overflow-hidden h-3 gap-0.5">
          {STATUS_ORDER.map((status) => {
            const count = breakdown[status] ?? 0;
            if (count === 0) return null;
            const pct = (count / total) * 100;
            const cfg = STATUS_CONFIG[status];
            return (
              <div
                key={status}
                style={{
                  width: `${pct}%`,
                  backgroundColor: cfg?.color ?? 'var(--text-muted)',
                  minWidth: '4px',
                }}
                title={`${cfg?.label}: ${count}`}
              />
            );
          })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {STATUS_ORDER.map((status) => {
            const count = breakdown[status] ?? 0;
            const cfg = STATUS_CONFIG[status];
            return (
              <div key={status} className="flex items-center gap-2">
                <div
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: cfg?.color ?? 'var(--text-muted)' }}
                />
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  {cfg?.label}
                </span>
                <span className="text-xs font-semibold font-ui" style={{ color: 'var(--text-primary)' }}>
                  {count}
                </span>
              </div>
            );
          })}
        </div>
      </CardBody>
    </Card>
  );
}

function RecentProjectRow({
  project,
  onOpen,
}: {
  project: { id: string; project_name: string; client_name: string; status: string; updated_at: string };
  onOpen: () => void;
}) {
  const cfg = STATUS_CONFIG[project.status] ?? STATUS_CONFIG.NEW;

  return (
    <Card
      isPressable
      onPress={onOpen}
      classNames={{
        base: 'border border-[var(--border)] transition-colors hover:border-[var(--border-em)]',
      }}
      style={{ backgroundColor: 'var(--bg-surface)' }}
    >
      <CardBody className="p-4 flex flex-row items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
              {project.project_name}
            </span>
            <Chip size="sm" color={cfg.chipColor} variant="flat" className="text-xs shrink-0">
              {cfg.label}
            </Chip>
          </div>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              {project.client_name}
            </span>
            <span className="text-xs flex items-center gap-1" style={{ color: 'var(--text-muted)' }}>
              <Clock size={11} />
              {relativeDate(project.updated_at)}
            </span>
          </div>
        </div>
        <span
          className="text-xs shrink-0 flex items-center gap-1"
          style={{ color: 'var(--text-muted)' }}
        >
          <FileText size={14} />
          Open
        </span>
      </CardBody>
    </Card>
  );
}

function LibraryCard({
  label,
  count,
  icon,
  href,
}: {
  label: string;
  count?: number;
  icon: React.ReactNode;
  href: string;
}) {
  const navigate = useNavigate();

  return (
    <Card
      isPressable
      onPress={() => navigate(href)}
      classNames={{
        base: 'border border-[var(--border)] transition-colors hover:border-[var(--border-em)]',
      }}
      style={{ backgroundColor: 'var(--bg-surface)' }}
    >
      <CardBody className="p-4 flex flex-row items-center gap-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{ backgroundColor: 'var(--bg-panel)', color: 'var(--accent)' }}
        >
          {icon}
        </div>
        <span className="text-sm font-medium flex-1" style={{ color: 'var(--text-primary)' }}>
          {label}
        </span>
        {count != null && (
          <Chip
            size="sm"
            variant="flat"
            classNames={{
              base: 'bg-[var(--bg-panel)] border border-[var(--border)]',
              content: 'text-[var(--text-secondary)] text-xs font-ui',
            }}
          >
            {count}
          </Chip>
        )}
        <ArrowRight size={14} style={{ color: 'var(--text-muted)' }} className="shrink-0" />
      </CardBody>
    </Card>
  );
}

function ProjectMiniCard({ project }: { project: any }) {
  const navigate = useNavigate();
  const cfg = STATUS_CONFIG[project.status] ?? STATUS_CONFIG.NEW;

  return (
    <Card
      isPressable
      onPress={() => navigate(`/projects/${project.id}`)}
      classNames={{
        base: 'border border-[var(--border)] transition-colors hover:border-[var(--border-em)]',
      }}
      style={{ backgroundColor: 'var(--bg-surface)' }}
    >
      <CardBody className="p-4 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Chip size="sm" color={cfg.chipColor} variant="flat" className="text-xs">
            {cfg.label}
          </Chip>
        </div>
        <h3
          className="text-sm font-semibold leading-snug line-clamp-2"
          style={{ color: 'var(--text-primary)' }}
        >
          {project.project_name}
        </h3>
        <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          {project.client?.name ?? 'Unknown Client'}
        </p>
      </CardBody>
    </Card>
  );
}

function EmptyState({ onNew }: { onNew: () => void }) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-4 py-16 rounded-xl border-2 border-dashed"
      style={{
        borderColor: 'var(--border)',
        backgroundColor: 'var(--bg-surface)',
      }}
    >
      <div
        className="w-14 h-14 rounded-full flex items-center justify-center"
        style={{ backgroundColor: 'var(--bg-panel)' }}
      >
        <FolderOpen size={26} style={{ color: 'var(--text-muted)' }} />
      </div>
      <div className="text-center">
        <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
          No projects yet
        </p>
        <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
          Create your first project to get started
        </p>
      </div>
      <Button
        color="primary"
        onPress={onNew}
        startContent={<Plus size={15} />}
        size="sm"
        className="font-medium mt-1"
      >
        New Project
      </Button>
    </div>
  );
}
